import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Avatar, IconButton, Typography, Badge, Paper, InputBase, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { useAuth } from '../../../../context/AuthContext';
import { useSystems } from '../../../../context/SystemContext';
import SharedPostCard, { isPostPayload } from '../post/SharedPostCard';

const API_BASE = "https://apiweb.hitpoly.com/ajax/chatCRMController.php";

// Componente Individual de Ventana de Chat
const FloatingChatWindow = ({ chat, onMinimize, onClose, myId, onDraftChange, senderProfileType }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState(chat.draft || "");
    const [loading, setLoading] = useState(true);
    const messagesEndRef = React.useRef(null);
    const activeConvIdRef = React.useRef(null);
    const messagesRef = React.useRef(messages);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        setNewMessage(chat.draft || "");
    }, [chat.draft]);

    const fetchMessages = useCallback(async () => {
        try {
            // Primero obtener o crear la conversación
            const resConv = await axios.post(API_BASE, {
                accion: 'iniciar_sesion',
                user_1_id: myId,
                user_2_id: chat.partner.id,
                profile_id: myId,
                profile_type: senderProfileType || chat.partner.type || 'professional',
                project_source: 'bolsa'
            });

            if (resConv.data.success) {
                const convId = resConv.data.conversation_id;
                activeConvIdRef.current = convId;
                const resMsg = await axios.post(API_BASE, {
                    accion: 'getMessages',
                    conversation_id: convId
                });
                
                if (resMsg.data.success) {
                    const newMessages = resMsg.data.data;
                    const currentMessages = messagesRef.current;
                    const hasChanged = currentMessages.length !== newMessages.length || 
                                       (currentMessages.length > 0 && currentMessages[currentMessages.length - 1]?.id !== newMessages[newMessages.length - 1]?.id);
                    
                    setMessages(newMessages);

                    if (hasChanged) {
                        window.dispatchEvent(new CustomEvent('chat-activity', {
                            detail: { conversationId: convId }
                        }));
                    }
                }
            } else {
            }
        } catch (e) {
        } finally {
            setLoading(false);
        }
    }, [myId, chat.partner.id]);

    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
    }, [fetchMessages]);

    useEffect(() => {
        const handleActivity = (e) => {
            const { conversationId } = e.detail || {};
            if (conversationId && String(activeConvIdRef.current) === String(conversationId)) {
                fetchMessages();
            }
        };
        window.addEventListener('chat-activity', handleActivity);
        return () => window.removeEventListener('chat-activity', handleActivity);
    }, [fetchMessages]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        const text = newMessage;
        setNewMessage("");
        onDraftChange(chat.id, ""); // Limpiar borrador en el padre

        try {
            const resConv = await axios.post(API_BASE, {
                accion: 'iniciar_sesion',
                user_1_id: myId,
                user_2_id: chat.partner.id,
                profile_id: myId,
                profile_type: senderProfileType || chat.partner.type || 'professional',
                project_source: 'bolsa'
            });

            if (resConv.data.success) {
                const convId = resConv.data.conversation_id;
                await axios.post(API_BASE, {
                    accion: 'sendMessage',
                    conversation_id: convId,
                    sender_id: myId,
                    sender_profile_id: myId,
                    sender_profile_type: senderProfileType || chat.partner.type || 'professional',
                    text: text
                });
                
                // Disparar evento de actividad inmediatamente tras enviar el mensaje
                window.dispatchEvent(new CustomEvent('chat-activity', {
                    detail: { conversationId: convId }
                }));

                fetchMessages();
            } else {
            }
        } catch (e) {
        }
    };

    return (
        <Paper 
            elevation={12}
            sx={{
                width: 320,
                height: 450,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px 16px 0 0',
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                bgcolor: '#fff',
                mr: 2,
                '@media (max-width: 768px)': {
                    width: '100%',
                    height: '100%',
                    borderRadius: 0,
                    mr: 0
                }
            }}
        >
            {/* Header */}
            <Box sx={{ p: 1.5, bgcolor: '#3B207F', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={chat.partner.avatar} sx={{ width: 28, height: 28, border: '2px solid rgba(255,255,255,0.2)' }}>
                        {chat.partner.name?.charAt(0)}
                    </Avatar>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, maxWidth: 160 }}>
                        {chat.partner.name}
                    </Typography>
                </Box>
                <Box>
                    <IconButton size="small" onClick={() => onMinimize(chat.id)} sx={{ color: '#fff' }}>
                        <RemoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => onClose(chat.id)} sx={{ color: '#fff' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Messages Area */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 2, bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography variant="caption">Cargando...</Typography></Box>
                ) : (
                    messages.map((msg, idx) => {
                        const isMe = msg.sender_id == myId;
                        return (
                            <Box key={msg.id || idx} sx={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                                <Box sx={{
                                    p: isPostPayload(msg.message_text) ? 0.5 : 1.2,
                                    borderRadius: isMe ? '15px 15px 2px 15px' : '15px 15px 15px 2px',
                                    bgcolor: isMe ? '#3B207F' : '#fff',
                                    color: isMe ? '#fff' : '#1a1a1a',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    border: isMe ? 'none' : '1px solid #eee',
                                    wordBreak: 'break-word',
                                    overflowX: 'hidden',
                                    '& p': { m: 0 }
                                }}>
                                    {isPostPayload(msg.message_text) ? (
                                        <SharedPostCard text={msg.message_text} isMe={isMe} />
                                    ) : (
                                    <div dangerouslySetInnerHTML={{
                                      __html: (() => {
                                        let text = msg.message_text || '';
                                        const linkColor = isMe ? '#fff' : '#3B207F';
                                        const aStyle = `color:${linkColor};font-weight:700;text-decoration:underline`;
                                        const imgExtRe = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?[^\s]*)?$/i;
                                        const lines = text.split('\n');
                                        const formatted = lines.map(line => {
                                          const trimmed = line.trim();
                                          if (imgExtRe.test(trimmed) && /^https?:\/\//i.test(trimmed)) {
                                            return `<img src="${trimmed}" alt="imagen" style="max-width:100%;max-height:300px;border-radius:8px;display:block;margin:4px 0" />`;
                                          }
                                          return trimmed.replace(
                                            /(<img[^>]*>|<a[^>]*>.*?<\/a>)|((https?:\/\/|www\.)[^\s<]+)/g,
                                            (match, tag, p1) => {
                                              if (tag) return tag;
                                              if (p1) {
                                                const href = p1.startsWith('http') ? p1 : `https://${p1}`;
                                                const isInternal = href.startsWith(window.location.origin) || href.startsWith('/');
                                                return `<a href="${href}"${isInternal ? '' : ' target="_blank" rel="noopener noreferrer"'} style="${aStyle}">${p1}</a>`;
                                              }
                                              return match;
                                            }
                                          );
                                        });
                                        return formatted.join('<br>');
                                      })()
                                    }} />
                                    )}
                                </Box>
                            </Box>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box sx={{ p: 1.5, borderTop: '1px solid #eee', display: 'flex', gap: 1, alignItems: 'center' }}>
                <InputBase
                    placeholder="Escribe un mensaje..."
                    value={newMessage}
                    onChange={(e) => {
                        const val = e.target.value;
                        setNewMessage(val);
                        onDraftChange(chat.id, val);
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    sx={{ flex: 1, fontSize: '0.85rem', bgcolor: '#f1f3f4', px: 1.5, py: 0.5, borderRadius: '20px' }}
                />
                <IconButton 
                    size="small" 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    sx={{ color: '#3B207F' }}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            </Box>
        </Paper>
    );
};

export const FloatingChatManager = () => {
    const { user } = useAuth();
    const { activeProfile } = useSystems();
    const [activeChats, setActiveChats] = useState([]);
    const globalUserId = user?.id || user?.user_id;
    const loadedUserRef = React.useRef(null);
    const skipSaveRef = React.useRef(true);

    // The effective myId comes from the active profile
    const effectiveMyId = activeProfile?.id || globalUserId;

    // Cargar chats guardados al cambiar de usuario
    useEffect(() => {
        if (globalUserId) {
            try {
                const saved = localStorage.getItem(`holding_active_chats_${globalUserId}`);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    setActiveChats(parsed);
                } else {
                    setActiveChats([]);
                }
            } catch (e) {
            }
            loadedUserRef.current = globalUserId;
            skipSaveRef.current = true; // Indicar que debemos omitir el guardado en el siguiente renderizado
        } else {
            setActiveChats([]);
            loadedUserRef.current = null;
            skipSaveRef.current = true;
        }
    }, [globalUserId]);

    // Guardar chats únicamente si pertenecen al usuario activo y ya fueron cargados
    useEffect(() => {
        if (!globalUserId) return;
        if (loadedUserRef.current !== globalUserId) return;
        if (skipSaveRef.current) {
            skipSaveRef.current = false;
            return;
        }

        try {
            localStorage.setItem(`holding_active_chats_${globalUserId}`, JSON.stringify(activeChats));
        } catch (e) {
        }
    }, [activeChats, globalUserId]);

    // Ref to ignore the next OPEN_FLOATING_CHAT event after a manual close
    const ignoreNextOpenRef = React.useRef(false);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === 'OPEN_FLOATING_CHAT') {
                // If a close action just occurred, ignore this opening event
                if (ignoreNextOpenRef.current) {
                    ignoreNextOpenRef.current = false;
                    return;
                }
                const { partner, myId } = event.data;
                const effectiveId = myId || effectiveMyId;
                setActiveChats(prev => {
                    // Si ya está abierto, no hacer nada o traer al frente
                    if (prev.find(c => c.partner.id === partner.id)) {
                        return prev.map(c => c.partner.id === partner.id ? { ...c, isMinimized: false } : c);
                    }
                    // Limitar a 3 chats abiertos por UX
                    const newChat = { id: Date.now(), partner, myId: effectiveId, isMinimized: false };
                    return [...prev, newChat].slice(-3);
                });
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [effectiveMyId]);

    const toggleMinimize = (chatId) => {
        setActiveChats(prev => prev.map(c => c.id === chatId ? { ...c, isMinimized: !c.isMinimized } : c));
    };

    const closeChat = (chatId) => {
        // Set flag to ignore the immediate reopen event that might be triggered elsewhere
        ignoreNextOpenRef.current = true;
        setActiveChats(prev => prev.filter(c => c.id !== chatId));
    };

    const handleDraftChange = (chatId, draftText) => {
        setActiveChats(prev => prev.map(c => c.id === chatId ? { ...c, draft: draftText } : c));
    };

    if (!globalUserId) return null;

    return (
        <>
            {/* Contenedor de Ventanas Abiertas (Abajo a la derecha, fluyendo a la izquierda) */}
            <Box sx={{
                position: 'fixed',
                bottom: 0,
                right: 80,
                display: 'flex',
                flexDirection: 'row-reverse',
                alignItems: 'flex-end',
                zIndex: 100002,
                pointerEvents: 'none',
                '@media (max-width: 768px)': {
                    top: 56,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    alignItems: 'stretch'
                }
            }}>
                {activeChats.filter(c => !c.isMinimized).map(chat => (
                    <Box key={chat.id} sx={{ pointerEvents: 'auto', '@media (max-width: 768px)': { width: '100%', flex: 1 } }}>
                        <FloatingChatWindow 
                            chat={chat} 
                            myId={effectiveMyId} 
                            onMinimize={toggleMinimize} 
                            onClose={closeChat} 
                            onDraftChange={handleDraftChange}
                            senderProfileType={activeProfile?.type}
                        />
                    </Box>
                ))}
            </Box>

            {/* Contenedor de Bolitas (Costado derecho) */}
            <Box sx={{
                position: 'fixed',
                right: 20,
                bottom: 120,
                display: 'flex',
                flexDirection: 'column-reverse',
                gap: 2,
                zIndex: 100003
            }}>
                {activeChats.filter(c => c.isMinimized).map(chat => (
                    <Tooltip key={chat.id} title={chat.partner.name} placement="left">
                        <Box sx={{ position: 'relative' }}>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                    <IconButton 
                                        size="small" 
                                        onClick={() => closeChat(chat.id)}
                                        sx={{ 
                                            width: 16, height: 16, bgcolor: '#ff4d4d', color: '#fff', 
                                            '&:hover': { bgcolor: '#ff0000' }, p: 0 
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 10 }} />
                                    </IconButton>
                                }
                            >
                                <Avatar
                                    src={chat.partner.avatar}
                                    onClick={() => toggleMinimize(chat.id)}
                                    sx={{ 
                                        width: 48, height: 48, cursor: 'pointer', 
                                        border: '3px solid #3B207F',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'scale(1.1)' }
                                    }}
                                >
                                    {chat.partner.name?.charAt(0)}
                                </Avatar>
                            </Badge>
                        </Box>
                    </Tooltip>
                ))}
            </Box>
        </>
    );
};
